import React, { useEffect, useState, useContext } from "react";
import { formatDateWithYear } from "../../../utils/hooks/dataHandler";
import { AuthContext } from "../../../contexts/AuthContext";
import { getCaSettings } from "../../../builders/settings/CaSetting";
import {
  caScheduleTypes,
  caScheduleDetails,
} from "../../../utils/static/CASchedule";
import CaSchedule from "../../../components/Schedules/CA/CaSchedules/CaSchedule";
import { useErrorDialog } from "../../../contexts/ErrorDialogContext";
import { useSuccessDialog } from "../../../contexts/SuccessDialogContext";
import ComponentLoading from "../../../components/App/ComponentLoading";
import { useAccessControl } from "../../../utils/hooks/useAccessControl";
import { CA_SCHEDULES } from "../../../utils/data/controlData";

const CaSchedulePage = ({ scheduleType, uen, scheduleId, company }) => {
  const {
    getCAScheduleList,
    getCAScheduleDataByType,
    updateCASchedule,
    deleteCAScheduleByType,
  } = useContext(AuthContext);
  const [saveTrigger, setSaveTrigger] = useState(false);
  const [schedule, setSchedule] = useState({});
  const [scheduleDetails, setScheduleDetails] = useState({});

  const { canEdit: canEditCASchedule } = useAccessControl(CA_SCHEDULES);

  const [loading, setLoading] = useState(true);

  const handleError = useErrorDialog();
  const handleSuccess = useSuccessDialog();

  const saveSchedule = async (schedule, scheduleDetails) => {
    if (!uen || !scheduleId) {
      handleError("Missing UEN or Schedule ID. Please try again.");
      return;
    }

    const updatedSchedule = {
      sch_id: scheduleId,
      scheduleType: caScheduleTypes[scheduleType],
      dataRows: {
        additions: scheduleDetails.additionsDetails,
        disposal: scheduleDetails.disposalDetails,
        hpAdditions: scheduleDetails.hirePurchaseAdditions,
        hpDisposal: scheduleDetails.hirePurchaseDisposal,
      },
      totalNetAA: scheduleDetails.totalNetAA,
      doc: scheduleDetails.doc,
      notes: schedule.notes,
      reviewNotes: schedule.reviewNotes,
    };

    setLoading(true);
    const response = await updateCASchedule(updatedSchedule);
    setLoading(false);

    if (response) {
      handleSuccess("The schedule has been saved successfully.");
      setSaveTrigger((prev) => !prev);
    } else {
      handleError(
        "There was a problem saving the schedule. Please contact support if the issue persists."
      );
    }
  };

  const deleteSchedule = async () => {
    if (!scheduleId) {
      handleError("Missing Schedule ID. Please try again.");
      return;
    }

    const updatedSchedule = {
      sch_id: scheduleId,
    };

    setLoading(true);
    const response = await deleteCAScheduleByType(updatedSchedule);
    setLoading(false);

    if (response) {
      handleSuccess("Schedule deleted successfully.");
      localStorage.removeItem("selectedSection");
      localStorage.setItem("selectedComponent", "Detailed P&L");
      window.location.reload();
    } else {
      handleError(
        "There was a problem saving the schedule. Please contact support if the issue persists."
      );
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      try {
        // Fetch schedule data from API
        const response = await getCAScheduleDataByType(scheduleId, caScheduleTypes[scheduleType]);

        const scheduleData = response?.schedule;

        let caSettings = getCaSettings(scheduleType.replace(/Schedule/g, ""));

        if (scheduleData) {
          setSchedule({
            id: scheduleId,
            type: caScheduleDetails[scheduleType].name,
            scheduleNo: scheduleData.scheduleNo,
            name: caScheduleDetails[scheduleType].name,
            caSettings: scheduleData.caSetting || caSettings,
            refSources: scheduleData.scheduleMapping,
            caType: caScheduleTypes[scheduleType].replace(/Schedule/g, ""),
            notes: scheduleData.notes || "",
            reviewNotes: scheduleData.reviewNotes || "",
          });

          setScheduleDetails({
            dataRows: scheduleData.dataRows,
            twdvSummary: scheduleData.TWDVSummary || [],
            faAdditionsSummary: scheduleData.faAdditionsSummary || {},
            faRegisterRows: scheduleData.faRegisterImport?.dataRows || [],
            hirePurchaseSummary: scheduleData.hirePurchaseSummary || {},
          });
        }
      } catch (error) {
        handleError(error);
      } finally {
        setLoading(false);
      }
    };

    if (uen && scheduleId) {
      fetchData();
    }
  }, [saveTrigger, scheduleId, uen]);

  const getAvailableCaScheduleList = async () => {
    const list = await getCAScheduleList(company.tc_id);

    const caList = list.filter(
      (schedule) => caScheduleTypes[schedule.scheduleType]
    );

    return caList;
  };

  return (
    <>
      {loading ? (
        <ComponentLoading />
      ) : (
        <CaSchedule
          company={company}
          schedule={schedule}
          scheduleDetails={scheduleDetails}
          saveSchedule={saveSchedule}
          deleteSchedule={deleteSchedule}
          canEditSchedule={canEditCASchedule}
          getAvailableCaScheduleList={getAvailableCaScheduleList}
          getCAScheduleDataByType={getCAScheduleDataByType}
          updateCASchedule={updateCASchedule}
          caScheduleTypes={caScheduleTypes}
        />
      )}
    </>
  );
};

export default CaSchedulePage;
